import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import theme from 'App.theme';
import Dialog from 'components/Dialog';
import PinPad, { PIN_LENGTH } from 'components/PinPad/PinPad';
import { useLanguage } from 'context/AppContext';
import { useEnrolPin } from 'hooks/useEnrolPin';
import { toPinAttemptFailure } from 'utils/helpers/pinUnlock.helpers';

type Props = {
  open: boolean;
  isChange?: boolean;
  onClose: (saved: boolean) => void;
};

type Step = 'terms' | 'enter' | 'confirm';

// Set or change the signed-in user's PIN: terms, choose a PIN, confirm it, then wrap + store
const EnrolPinDialog: FC<Props> = ({ open, isChange, onClose }) => {
  const lang = useLanguage()[0].PinLogin.enrol;
  const enrolPin = useEnrolPin();

  const [step, setStep] = useState<Step>('terms');
  const [accepted, setAccepted] = useState(false);
  const [firstPin, setFirstPin] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('terms');
      setAccepted(false);
      setFirstPin('');
      setPin('');
      setError(undefined);
    }
  }, [open]);

  const onPinChange = useCallback(
    async (value: string) => {
      setPin(value);
      setError(undefined);
      if (value.length < PIN_LENGTH) return;

      if (step === 'enter') {
        setFirstPin(value);
        setPin('');
        setStep('confirm');
        return;
      }

      if (value !== firstPin) {
        setError(lang.mismatch);
        setFirstPin('');
        setPin('');
        setStep('enter');
        return;
      }

      setBusy(true);
      try {
        await enrolPin(value);
        toast.success(lang.success);
        onClose(true);
      } catch (e) {
        const failure = toPinAttemptFailure(e);
        const message =
          failure?.status === 'locked_out' ? lang.lockedOut({ seconds: failure.retryAfterSeconds }) : lang.error;
        toast.error(message);
        setError(message);
        setFirstPin('');
        setStep('enter');
      } finally {
        setPin('');
        setBusy(false);
      }
    },
    [step, firstPin, enrolPin, lang, onClose],
  );

  return (
    <Dialog
      open={open}
      title={isChange ? lang.changeTitle : lang.setTitle}
      onCancel={() => onClose(false)}
      onClose={() => onClose(false)}
      confirmLabel={lang.next}
      onConfirm={step === 'terms' ? () => setStep('enter') : undefined}
      confirmDisabled={!accepted}
      body={
        step === 'terms' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.layout.spacing.medium }}>
            <Typography>{lang.terms}</Typography>
            <FormControlLabel
              control={<Checkbox checked={accepted} onChange={(_, checked) => setAccepted(checked)} />}
              label={lang.acceptTerms}
            />
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.layout.spacing.medium }}
          >
            <Typography>{step === 'enter' ? lang.enterPin : lang.confirmPin}</Typography>
            <PinPad value={pin} onChange={onPinChange} disabled={busy} />
            <Box sx={{ height: 24 }}>{busy && <CircularProgress size={24} aria-label={lang.saving} />}</Box>
            <Typography color="error" minHeight="1.5em" role="alert">
              {error}
            </Typography>
          </Box>
        )
      }
    />
  );
};

export default EnrolPinDialog;
